/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AddIcon, ArrowDownBold, CloseCircleGreyIcon, CopyLinkIcon } from 'assets';
import { Input, RadioInput } from 'components/form';
import { OptionType } from 'components/form/select';
import { DropdownMenu, DropdownMenuOption } from 'components/dropdown-menu';
import { useToast } from 'hooks';

// Types
type Role = 'Recruiter' | 'Viewer';

interface Collaborator {
  id: string;
  email: string;
  role: Role;
  jobsToView?: string[];
}

interface FormData {
  email: string;
  jobs: string[];
}

// Validation Schema
const schema: yup.ObjectSchema<FormData> = yup
  .object({
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    jobs: yup
      .array(yup.string().defined())
      .max(4, 'Select up to 4 jobs')
      .required()
      .default([]),
  })
  .required();

const jobOptions: OptionType[] = [
  { label: 'All Jobs', value: 'All Jobs' },
  { label: 'HR Manager', value: 'HR Manager' },
  { label: 'Customer Representative', value: 'Customer Representative' },
  { label: 'Customer Relations Manager(CRM)', value: 'Customer Relations Manager(CRM)' },
  { label: 'Product manager', value: 'Product manager' },
];

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const { openToast } = useToast();
  // useClickOutside(modalRef, onClose);
  const [selectedRole, setSelectedRole] = useState<Role>('Recruiter');
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      email: '',
      jobs: [],
    },
  });

  const emailValue = watch('email');
  const watchJobs = (watch('jobs') as string[]) ?? [];

  const selectedJobsText = watchJobs.includes('All Jobs')
    ? 'All Jobs'
    : watchJobs.length === 0
      ? 'Select job'
      : watchJobs.length === 1
        ? watchJobs[0]
          ? String(watchJobs[0])
          : 'Select job'
        : `${watchJobs.length} selected`;

  const computeJobsToView = (): string[] => {
    if (watchJobs.includes('All Jobs')) {
      return jobOptions.slice(1).map(job => job.value);
    }
    return watchJobs;
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const jobsToView = computeJobsToView();
    const newCollaborator: Collaborator = {
      id: Date.now().toString(),
      email: data.email,
      role: selectedRole,
      ...(selectedRole === 'Viewer' && jobsToView.length > 0
        ? { jobsToView }
        : {}),
    };
    setCollaborators([...collaborators, newCollaborator]);
    reset({ email: '', jobs: [] });
  };

  const updateCollaboratorRole = (id: string, newRole: Role) => {
    setCollaborators(
      collaborators.map((c) =>
        c.id === id
          ? {
            ...c,
            role: newRole,
            ...(newRole === 'Recruiter' ? { jobsToView: undefined } : {}),
          }
          : c
      )
    );
  };

  const updateCollaboratorJobs = (id: string, selectedJobs: string[]) => {
    const jobsToView = selectedJobs.includes('All Jobs')
      ? jobOptions.slice(1).map(job => job.value)
      : selectedJobs;

    setCollaborators(
      collaborators.map((c) =>
        c.id === id
          ? { ...c, jobsToView: jobsToView.length > 0 ? jobsToView : undefined }
          : c
      )
    );
  };

  const handleInviteCollaborators = () => {
    if (collaborators.length === 0) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Please add at least one collaborator to invite',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    openToast({
      show: true,
      heading: 'Success',
      text: `Successfully invited ${collaborators.length} collaborator${collaborators.length > 1 ? 's' : ''}`,
      type: 'success',
      timeOut: 5000,
    });

    // Reset form and collaborators
    setCollaborators([]);
    reset({ email: '', jobs: [] });
    setTimeout(() => {
      onClose();
    }, 500);
    
  };

  const roleDropdownOptions = (onSelect: (role: Role) => void): DropdownMenuOption[] => [
    { key: 'Recruiter', text: 'Recruiter', onClick: () => onSelect('Recruiter') },
    { key: 'Viewer', text: 'Viewer', onClick: () => onSelect('Viewer') },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Share</h2>
          <button
            onClick={onClose}
            className="transition-colors !bg-transparent !p-0 !w-fit !h-fit"
          >
            <CloseCircleGreyIcon className='text-[#5a5a66]' />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto sm:p-6 p-4">
          {/* Add Collaborators Section */}
          <div className="mb-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Add collaborators with email..."
                  validatorMessage={errors.email?.message}
                  className="w-full sm:!h-[50px] !h-[40px]"
                  parentClassName='!mb-0'
                  name="email"
                  register={register}
                  value={emailValue}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={!!errors.email || !emailValue}
                className="px-6 sm:h-[50px] h-[40px] !bg-[#012169] text-white rounded-lg hover:bg-[#012169]/80 transition-colors font-medium disabled:opacity-20"
              >
                Add
              </button>
            </div>
          </div>

          {/* Assign Role Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Assign Role</h3>
            <div className="bg-[#EFF8FF] rounded-lg p-4">
              <DropdownMenu
                options={roleDropdownOptions(setSelectedRole)}
                trigger={
                  <button
                    type="button"
                    className="cursor-pointer flex items-center gap-2 mb-3 text-base font-semibold text-gray-900 hover:bg-[#EFF8FF] !bg-[#EFF8FF] !p-0 !h-fit !w-fit"
                  >
                    <span>{selectedRole}</span>
                    <ArrowDownBold className="w-3 h-3" />
                  </button>
                }
                menuClassName='!p-0'
                menu={(close) => (
                  <div className='w-full'>
                    {roleDropdownOptions(setSelectedRole).map(option => (
                      <div key={option.key}>
                        <p
                          onClick={() => {
                            option.onClick();
                            close();
                          }}
                          className='text-left text-[#475467] w-full py-2 px-4 hover:bg-[#D1E9FF] hover:border-l-4 rounded-[4px] border-[#53B1FD] cursor-pointer text-sm'
                        >
                          {option.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              />
              <p className="text-sm text-[#475467] mb-3">
                {selectedRole === 'Recruiter'
                  ? 'The person can Create and manage roles, invite candidates, review assessments, collaborate on shortlisting, and add comments or tags.'
                  : 'The person can view reports, shortlist, performance summaries and add comments.'}
              </p>
              {selectedRole === 'Viewer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Job to view:</label>
                  <DropdownMenu
                    options={[]}
                    align='left'
                    trigger={
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 h-[25px] min-h-[25px] border border-[#98A2B3] rounded px-2 pl-2 pr-2 text-sm text-[#101828] bg-white hover:bg-white"
                      >
                        <span className="truncate max-w-[220px]">{selectedJobsText}</span>
                        <ArrowDownBold className="w-3 h-3" />
                      </button>
                    }
                    menuClassName="!p-0"
                    menu={(close) => (
                      <div className="">
                        {jobOptions.length > 0 && (
                          <RadioInput
                            name="jobs"
                            type="checkbox"
                            register={register}
                            validatorMessage={errors.jobs?.message as string}
                            label=""
                            options={jobOptions}
                            parentClassName="!p-0 !m-5"
                            showRequired={false}
                            disabled={(watchJobs?.length || 0) >= 4}
                            checkValue={watchJobs || []}
                          />
                        )}
                        <div className="m-5 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            className="text-sm justify-center text-[#475467] hover:text-gray-900 !bg-transparent"
                            onClick={() => setValue('jobs', [])}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1.5 text-sm justify-center font-medium !text-white !bg-[#012169] rounded hover:bg-[#011557]"
                            onClick={close}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Collaborators List */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Collaborators
              <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-medium bg-[#012169] text-white rounded-full">
                {collaborators.length}
              </span>
            </h3>
            <div className="border border-[#EAECF0] rounded-lg p-3">
              {collaborators.length === 0 ? (
                <p className="text-sm text-[#475467] text-center py-4">No collaborators added yet</p>
              ) : (
                collaborators.map((collaborator) => (
                  <div key={collaborator.id} className="flex items-center gap-3 justify-between py-2 w-full">
                    <div className="flex sm:items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#EFF8FF] border-dashed border border-[#98A2B3] flex items-center justify-center text-[#667085] flex-shrink-0 font-medium">
                        E
                      </div>
                      <div className=''>
                        <p className="text-sm text-[#344054]">{collaborator.email}</p>
                        <div className='flex items-end gap-2 mt-1'>
                          {collaborator.role === 'Viewer' &&collaborator.jobsToView && collaborator.jobsToView.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              {collaborator.jobsToView.map((job, index) => (
                                <span key={index} className="text-xs text-[#475467] bg-gray-100 px-2 py-0.5 rounded">
                                  {job}
                                </span>
                              ))}
                            </div>
                          )}
                          {collaborator.role === 'Viewer' &&
                            <CollaboratorJobDropdown
                              collaborator={collaborator}
                              onUpdate={updateCollaboratorJobs}
                              register={register}
                              watch={watch}
                              setValue={setValue}
                            />
                          }
                        </div>

                      </div>
                    </div>

                    <DropdownMenu
                      options={roleDropdownOptions((role) => updateCollaboratorRole(collaborator.id, role))}
                      trigger={
                        <button
                          type="button"
                          className="inline-flex items-center gap-2.5 h-[25px] min-h-[25px] !border-none rounded px-2 pl-2 pr-2 text-sm text-[#101828] bg-white hover:bg-white"
                        >
                          <span>{collaborator.role}</span>
                          <ArrowDownBold className="w-3 h-3" />
                        </button>
                      }
                      menuClassName='!p-0'
                      menu={(close) => (
                        <div className='w-full'>
                          {roleDropdownOptions((role) => updateCollaboratorRole(collaborator.id, role)).map(option => (
                            <div key={option.key}>
                              <p
                                onClick={() => {
                                  option.onClick();
                                  close();
                                }}
                                className='text-left text-[#475467] w-full py-2 px-4 hover:bg-[#D1E9FF] hover:border-l-4 rounded-[4px] border-[#53B1FD] cursor-pointer text-sm'
                              >
                                {option.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sm:p-6 p-4 border-t border-gray-200 flex sm:flex-row flex-col sm:justify-end gap-3 sm:items-center">
          <div className="flex gap-3 sm:ml-auto">
            <button className="flex items-center gap-2 !bg-transparent px-4 py-2 text-sm font-medium text-[#012169] border border-[#012169] rounded-md hover:bg-[#F9FAFB] transition-colors !ml-0">
              <CopyLinkIcon className="w-4 h-4 text-[#012169]" />
              Copy Link
            </button>
            {collaborators.length > 0 && (
              <button
                onClick={handleInviteCollaborators}
                className="px-6 py-2 text-sm font-medium text-white bg-[#012169] rounded-lg hover:bg-[#011557] transition-colors"
              >
                Invite {collaborators.length} Collaborator{collaborators.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Separate component for collaborator job dropdown to manage local state
interface CollaboratorJobDropdownProps {
  collaborator: Collaborator;
  onUpdate: (id: string, selectedJobs: string[]) => void;
  register: any;
  watch: any;
  setValue: any;
}

const CollaboratorJobDropdown: React.FC<CollaboratorJobDropdownProps> = ({
  collaborator, onUpdate, register, watch, setValue
}) => {
  const uniqueFieldName = `jobs-${collaborator.id}`;
  const [localJobs, setLocalJobs] = useState<string[]>(collaborator.jobsToView || []);

  const watched = watch(uniqueFieldName);

  useEffect(() => {
    setLocalJobs(watched ?? []);
  }, [watched]);

  useEffect(() => {
    register(uniqueFieldName);
    setValue(uniqueFieldName, collaborator.jobsToView || []);
  }, [uniqueFieldName, collaborator.jobsToView]);


  return (
    <DropdownMenu
      options={[]}
      align="left"
      trigger={
        <button
          type="button"
          className="text-[#475467] !bg-transparent hover:text-gray-900 !p-0 !h-fit !w-fit"
        >
          <AddIcon className="w-3 h-3" />
        </button>
      }
      menuClassName="!p-0"
      menu={(close) => (
        <div className="">
          <RadioInput
            name={uniqueFieldName}
            type="checkbox"
            register={register}
            validatorMessage=""
            label=""
            options={jobOptions}
            parentClassName="!p-0 !m-5"
            showRequired={false}
            disabled={false}
            checkValue={localJobs}
          />
          <div className="m-5 flex items-center justify-between gap-2">
            <button
              type="button"
              className="text-sm justify-center text-[#475467] hover:text-gray-900 !bg-transparent"
              onClick={() => {
                setLocalJobs([]);
                setValue(uniqueFieldName, []); // 🧼 clear form value too
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm justify-center font-medium !text-white !bg-[#012169] rounded hover:bg-[#011557]"
              onClick={() => {
                onUpdate(collaborator.id, localJobs);
                close();
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    />
  );
};


export { ShareModal };